import { useEffect, useState, type FormEvent } from 'react';
import type { KonfigurasiHonorJabatan, Jabatan } from '@app/shared';
import { AdminLayout } from '../../components/admin-layout';
import { Alert, Button, Card, Input, Modal, Table, Td, Th, Select } from '../../components/ui';
import {
  getKonfigurasiHonorJabatanList,
  createKonfigurasiHonorJabatan,
  updateKonfigurasiHonorJabatan,
  deleteKonfigurasiHonorJabatan,
} from '../../features/konfigurasi-honor-jabatan/api';
import { getJabatanList } from '../../features/master/api';

const emptyForm = {
  jabatan_id: 0,
  honor_jabatan: 0,
  berlaku_mulai: '',
  berlaku_sampai: '',
};

function formatRupiah(value: number): string {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0,
    maximumFractionDigits: 0,
  }).format(value);
}

export function KonfigurasiHonorJabatanPage() {
  const [items, setItems] = useState<KonfigurasiHonorJabatan[]>([]);
  const [jabatanList, setJabatanList] = useState<Jabatan[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [message, setMessage] = useState<string | null>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [editing, setEditing] = useState<KonfigurasiHonorJabatan | null>(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);

  const load = async () => {
    setLoading(true);
    try {
      const [result, jabatan] = await Promise.all([
        getKonfigurasiHonorJabatanList({ limit: 100 }),
        getJabatanList({ limit: 100, status_aktif: true }),
      ]);
      setItems(result.rows);
      setJabatanList(jabatan.rows);
      setError(null);
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal memuat konfigurasi');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    void load();
  }, []);

  const openAdd = () => {
    setEditing(null);
    setForm(emptyForm);
    setModalOpen(true);
  };

  const openEdit = (item: KonfigurasiHonorJabatan) => {
    setEditing(item);
    setForm({
      jabatan_id: item.jabatan_id,
      honor_jabatan: item.honor_jabatan,
      berlaku_mulai: item.berlaku_mulai ?? '',
      berlaku_sampai: item.berlaku_sampai ?? '',
    });
    setModalOpen(true);
  };

  const submit = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.jabatan_id || !form.honor_jabatan || !form.berlaku_mulai) {
      setError('Jabatan, honor, dan tanggal berlaku wajib diisi');
      return;
    }
    setSaving(true);
    setError(null);
    setMessage(null);
    try {
      if (editing) {
        await updateKonfigurasiHonorJabatan(editing.id, {
          honor_jabatan: form.honor_jabatan,
          berlaku_sampai: form.berlaku_sampai || null,
        });
        setMessage('Konfigurasi diperbarui.');
      } else {
        await createKonfigurasiHonorJabatan({
          jabatan_id: form.jabatan_id,
          honor_jabatan: form.honor_jabatan,
          berlaku_mulai: form.berlaku_mulai,
          berlaku_sampai: form.berlaku_sampai || null,
        });
        setMessage('Konfigurasi ditambahkan.');
      }
      setModalOpen(false);
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menyimpan konfigurasi');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id: number) => {
    if (!confirm('Hapus konfigurasi ini?')) return;
    try {
      await deleteKonfigurasiHonorJabatan(id);
      setMessage('Konfigurasi dihapus.');
      await load();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Gagal menghapus konfigurasi');
    }
  };

  const getJabatanLabel = (jabatanId: number) => {
    const j = jabatanList.find((x) => x.id === jabatanId);
    return j ? `${j.kode} - ${j.nama}` : `Jabatan #${jabatanId}`;
  };

  return (
    <AdminLayout title="Konfigurasi Honor Jabatan">
      {error && <Alert type="error">{error}</Alert>}
      {message && <Alert type="success">{message}</Alert>}
      <Card>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '1rem' }}>
          <p style={{ margin: 0, color: '#64748B' }}>
            Tarif honor jabatan temporal (berlaku mulai — berlaku sampai). Setiap jabatan bisa
            memiliki beberapa konfigurasi untuk periode berbeda.
          </p>
          <Button onClick={openAdd}>Tambah Konfigurasi</Button>
        </div>
        {loading ? (
          <p>Memuat…</p>
        ) : items.length === 0 ? (
          <p style={{ color: '#64748B' }}>Belum ada konfigurasi. Tambahkan item pertama.</p>
        ) : (
          <Table>
            <thead>
              <tr>
                <Th>Jabatan</Th>
                <Th>Honor Jabatan</Th>
                <Th>Berlaku Mulai</Th>
                <Th>Berlaku Sampai</Th>
                <Th style={{ width: 180 }}>Aksi</Th>
              </tr>
            </thead>
            <tbody>
              {items.map((item) => (
                <tr key={item.id}>
                  <Td>
                    {item.jabatan_kode && item.jabatan_nama
                      ? `${item.jabatan_kode} - ${item.jabatan_nama}`
                      : getJabatanLabel(item.jabatan_id)}
                  </Td>
                  <Td>
                    <span style={{ fontFamily: 'monospace' }}>
                      {formatRupiah(item.honor_jabatan)}
                    </span>
                  </Td>
                  <Td>{item.berlaku_mulai}</Td>
                  <Td>{item.berlaku_sampai ?? '-'}</Td>
                  <Td>
                    <div style={{ display: 'flex', gap: '0.5rem' }}>
                      <Button variant="secondary" onClick={() => openEdit(item)}>
                        Ubah
                      </Button>
                      <Button variant="danger" onClick={() => void remove(item.id)}>
                        Hapus
                      </Button>
                    </div>
                  </Td>
                </tr>
              ))}
            </tbody>
          </Table>
        )}
      </Card>
      <Modal
        open={modalOpen}
        onClose={() => setModalOpen(false)}
        title={editing ? 'Ubah Konfigurasi' : 'Tambah Konfigurasi'}
      >
        <form onSubmit={submit}>
          {!editing ? (
            <Select
              label="Jabatan"
              name="jabatan_id"
              value={String(form.jabatan_id)}
              onChange={(e) => setForm({ ...form, jabatan_id: Number(e.target.value) })}
            >
              <option value="0">-- Pilih Jabatan --</option>
              {jabatanList.map((j) => (
                <option key={j.id} value={String(j.id)}>
                  {j.kode} - {j.nama}
                </option>
              ))}
            </Select>
          ) : (
            <div style={{ marginBottom: '1rem' }}>
              <label
                style={{
                  display: 'block',
                  fontWeight: 600,
                  marginBottom: '0.25rem',
                  fontSize: '0.875rem',
                }}
              >
                Jabatan
              </label>
              <p style={{ margin: 0, color: '#64748B' }}>
                {editing.jabatan_kode && editing.jabatan_nama
                  ? `${editing.jabatan_kode} - ${editing.jabatan_nama}`
                  : getJabatanLabel(editing.jabatan_id)}
              </p>
            </div>
          )}
          <Input
            label="Honor Jabatan (Rp)"
            name="honor_jabatan"
            type="number"
            min={0}
            value={String(form.honor_jabatan)}
            onChange={(e) => setForm({ ...form, honor_jabatan: Number(e.target.value) })}
            required
          />
          <Input
            label="Berlaku Mulai"
            name="berlaku_mulai"
            type="date"
            value={form.berlaku_mulai}
            onChange={(e) => setForm({ ...form, berlaku_mulai: e.target.value })}
            required={!editing}
            disabled={!!editing}
          />
          <Input
            label="Berlaku Sampai"
            name="berlaku_sampai"
            type="date"
            value={form.berlaku_sampai}
            onChange={(e) => setForm({ ...form, berlaku_sampai: e.target.value })}
          />
          <p style={{ fontSize: '0.8rem', color: '#64748B', marginTop: '-0.5rem' }}>
            Kosongkan jika tidak ada batas akhir.
          </p>
          <Button type="submit" disabled={saving}>
            {saving ? 'Menyimpan…' : 'Simpan'}
          </Button>
        </form>
      </Modal>
    </AdminLayout>
  );
}
